/**
 * Citas clicables en las respuestas del chat.
 *
 * Diferencia con LicitAI: aquí las citas NO salen de un regex sobre
 * `[Fuente N]` que emite el LLM, sino de eventos SSE `citation` que el
 * backend manda durante el streaming. El hook de chat inserta un marcador
 * `[[cita:N]]` en el punto del texto en el que llegó cada evento (N =
 * posición 1-based en la lista de fuentes del mensaje).
 *
 * Estrategia:
 *   - `linkifyCitations(text, messageId)` transforma `[[cita:N]]` en
 *     `[N](#source-N-<msg>)` para que React-Markdown lo renderice como
 *     link y le demos estilo de chip vía `components.a`. Como fallback
 *     para mensajes históricos también tolera `[Fuente N]`.
 *   - `handleCitationClick` intercepta el click, scroll suave a la fuente
 *     y flash visual (ring Amarillo Energía) — misma UX que LicitAI.
 *   - `sourceAnchorId(n, messageId)` produce el id consistente
 *     (id="source-N-<msg>") de la lista de fuentes.
 */
import type { MouseEvent } from "react";

export function citationMarker(n: number): string {
  return `[[cita:${n}]]`;
}

export function linkifyCitations(content: string, messageId: string): string {
  return content
    .replace(/\[\[cita:(\d+)\]\]/g, (_, n) => `[${n}](#source-${n}-${messageId})`)
    .replace(/\[Fuente\s+(\d+)\]/g, (_, n) => `[${n}](#source-${n}-${messageId})`);
}

export function sourceAnchorId(num: number, messageId: string): string {
  return `source-${num}-${messageId}`;
}

/** Extrae N de un href `#source-N-<msg>`, o null si no es un chip de cita. */
export function citationNumberFromHref(href: string | undefined): number | null {
  if (!href || !href.startsWith("#source-")) return null;
  const m = href.match(/^#source-(\d+)-/);
  return m ? Number(m[1]) : null;
}

const HIGHLIGHT_CLASSES = ["ring-2", "ring-energia", "ring-offset-2"];

export function handleCitationClick(e: MouseEvent<HTMLAnchorElement>) {
  const href = e.currentTarget.getAttribute("href") || "";
  if (!href.startsWith("#source-")) return;
  e.preventDefault();
  const target = document.getElementById(href.slice(1));
  if (!target) return;
  // Las fuentes van en un <details> colapsado: ábrelo para revelar la fuente.
  const details = target.closest("details");
  if (details && !details.open) details.open = true;
  target.scrollIntoView({ behavior: "smooth", block: "center" });
  target.classList.add(...HIGHLIGHT_CLASSES);
  window.setTimeout(() => {
    target.classList.remove(...HIGHLIGHT_CLASSES);
  }, 1600);
}
